/**
 * The Optimization card: the optimization preset (the exploration objective
 * row) and the settings of the exploration config that follows it.
 *
 * The preset sets the defaults; everything the optimizer reads is then a
 * control here. The run settings (population, iterations) are fields. The
 * optimizer (optimization.algorithms[0]) is a choice with its settings as
 * fields under it (helper/optimizerChoice.js). Every objective the
 * algorithms package knows and every rejection rule it applies is a row
 * with a switch: on means the key is in the document (with its settings
 * next to the switch), off means it is not. The hue-wheel ranking is a row
 * too. Nothing else in the document is touched (Configurations card).
 */
import React, { useMemo, useRef } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import ChoiceCard from "./ChoiceCard";
import SettingFields, { deleteAt, getAt, setAt, useInvalidTotal } from "./SettingFields";
import ToggleRows from "./ToggleRows";
import { HAIRLINE, INK } from "../../../components/cardChrome";
import {
  OPTIMIZERS,
  SURROGATE_DEFAULTS,
  algorithmBlocks,
  algorithmName,
  optimizerOf,
  withOptimizer,
} from "../../../helper/optimizerChoice";

const OBJECTIVES_PATH = ["optimization", "objectives"];
const RULES_PATH = ["optimization", "rejection_policies"];
const RANKING_PATH = ["optimization", "ranking"];
const GA_PATH = ["optimization", "algorithms", 0, "genetic_algorithm"];
const PARENT_PATH = [...GA_PATH, "parent_selection"];
const PRESCREEN_PATH = [...GA_PATH, "surrogate_prescreen"];
const SURROGATE_PATH = ["optimization", "algorithms", 0, "surrogate_coverage_search"];

/** The design-space parameters the learned rules read, in the order the
 *  server stores them (measure_color_step._LASER_PARAM_ORDER). */
export const FEATURE_NAMES = ["frequency", "speed", "power", "pulse_duration", "pass_count", "hatching", "line_count"];

/** The run fields; the population size must fill whole patch groups of the
 *  plate design (start_exploration lays the points out rows × columns per
 *  group), so it is checked against the grid when one is known. */
export function runFields(patchGrid) {
  const rows = Array.isArray(patchGrid) ? Number(patchGrid[0]) : NaN;
  const cols = Array.isArray(patchGrid) ? Number(patchGrid[1]) : NaN;
  const perGroup = rows > 0 && cols > 0 ? rows * cols : null;
  return [
    {
      path: ["optimization", "population_policy", "start_population_size"],
      label: "Population size",
      kind: "integer",
      min: 1,
      help: perGroup ? `Sample points per iteration, whole patch groups of ${perGroup}` : "Sample points marked per iteration",
      validate: perGroup
        ? (value) => (value % perGroup === 0 ? null : `Multiple of ${perGroup} (${rows} × ${cols} patches per group)`)
        : undefined,
    },
    ...RUN_FIELDS_REST,
  ];
}

const RUN_FIELDS_REST = [
  {
    path: ["expected_iterations"],
    label: "Planned iterations",
    kind: "integer",
    min: 1,
    help: "Progress display only; the run can go on",
  },
];

// The second parent's selection (genetic_algorithm_optimization.py
// MATE_SELECTIONS) and its defaults, which the GA applies without the keys.
const MATE_BY_PROXIMITY = "design_space_proximity";
const PARENT_DEFAULTS = { mate_neighbours: 5, unrestricted_share: 0.25 };

/** The genetic algorithm's settings; the neighbour settings only with proximity mating. */
export function geneticAlgorithmFields(config) {
  const proximity = getAt(config, [...PARENT_PATH, "mate"]) === MATE_BY_PROXIMITY;
  return [
    {
      path: [...GA_PATH, "crossing_probability"],
      label: "Crossing probability",
      min: 0,
      max: 1,
      help: "Else the better parent is cloned and mutated",
    },
    {
      path: [...PARENT_PATH, "mate"],
      label: "Second parent",
      kind: "choice",
      default: "rank",
      options: [
        { value: "rank", label: "By score" },
        { value: MATE_BY_PROXIMITY, label: "Near the first in the design space" },
      ],
    },
    ...(proximity
      ? [
          {
            path: [...PARENT_PATH, "mate_neighbours"],
            label: "Neighbours of the first parent",
            kind: "integer",
            min: 1,
            help: "The second parent is drawn from these",
          },
          {
            path: [...PARENT_PATH, "unrestricted_share"],
            label: "Share drawn by score",
            min: 0,
            max: 1,
            help: "Keeps some crossing of distant parents",
          },
        ]
      : []),
    {
      path: [...RANKING_PATH, "tie_break"],
      label: "Tie-break",
      kind: "choice",
      default: "input_order",
      options: [
        { value: "input_order", label: "Input order" },
        { value: "gamut_growth", label: "Gamut growth" },
      ],
      help: "Points with the same rank: survivors first, or most added hull volume",
    },
  ];
}

/** The colour model's fixed scale and noise floor (surrogate_coverage/surrogate.py); the floor must stay below the scale. */
function colourModelFields(config, basePath) {
  const scale = Number(getAt(config, [...basePath, "colour_scale_delta_e"]));
  return [
    {
      path: [...basePath, "colour_scale_delta_e"],
      label: "Colour scale",
      exclusiveMin: 0,
      unit: "ΔE",
      help: "Spread of L*, a*, b* assumed before measuring",
    },
    {
      path: [...basePath, "noise_floor_delta_e"],
      label: "Noise floor",
      min: 0,
      unit: "ΔE",
      help: "Per channel, until re-marked designs measure it",
      validate: (value) => (scale > 0 && value >= scale ? "Below the colour scale" : null),
    },
  ];
}

const prescreen = (key, rest) => ({ ...rest, path: [...PRESCREEN_PATH, key] });

/** genetic_algorithm.surrogate_prescreen (surrogate_coverage/prescreen.py). */
export function prescreenFields(config) {
  return [...PRESCREEN_FIELDS, ...colourModelFields(config, PRESCREEN_PATH)];
}

const PRESCREEN_FIELDS = [
  prescreen("oversampling", {
    label: "Offspring bred per patch",
    kind: "integer",
    min: 1,
    help: "The model keeps the most promising",
  }),
  prescreen("cell_edge_delta_e", {
    label: "Colour cell edge",
    exclusiveMin: 0,
    unit: "ΔE",
    help: "About the repeat ΔE of one measurement",
  }),
  prescreen("minimum_usable_for_model", {
    label: "Usable patches before the model",
    kind: "integer",
    min: 1,
    help: "Fewer: offspring are marked unscreened",
  }),
  prescreen("max_fit_points", {
    label: "Patches for the model fit",
    kind: "integer",
    min: 1,
    help: "Predictions still use every patch",
  }),
  prescreen("seed", { label: "Random seed", kind: "integer", min: 0, clearable: true, help: "Empty: different every run" }),
];

const surrogate = (key, rest) => ({ ...rest, path: [...SURROGATE_PATH, key] });

const COMBINED = "combined";
// A block without an aim runs with the algorithm's default.
const surrogateAim = (config) => getAt(config, [...SURROGATE_PATH, "acquisition"]) ?? SURROGATE_DEFAULTS.acquisition;
// Settings of the exterior aim, and the aims that read them.
const AIM_SETTINGS = {
  hull_exterior_share: [COMBINED],
  hull_exterior_min_p_usable: ["hull_exterior", COMBINED],
};
const aimReads = (config, key) => AIM_SETTINGS[key].includes(surrogateAim(config));

/** surrogate_coverage_search (surrogate_coverage/optimizer.py DEFAULTS), by topic; the exterior aim's settings only with the aims that read them. */
export function surrogateGroups(config) {
  const [coverage, ...rest] = SURROGATE_GROUPS;
  const model = rest[rest.length - 1];
  const fields = [...coverage.fields];
  const exterior = [
    aimReads(config, "hull_exterior_share") &&
      surrogate("hull_exterior_share", {
        label: "Share outside the hull",
        min: 0,
        max: 1,
        help: "Of the patches the model chooses",
      }),
    aimReads(config, "hull_exterior_min_p_usable") &&
      surrogate("hull_exterior_min_p_usable", {
        label: "Usability outside the hull",
        min: 0,
        max: 1,
        help: "Least predicted probability of a usable patch",
      }),
  ].filter(Boolean);
  fields.splice(2, 0, ...exterior);
  return [
    { ...coverage, fields },
    ...rest.slice(0, -1),
    { ...model, fields: [...model.fields.slice(0, 3), ...colourModelFields(config, SURROGATE_PATH), ...model.fields.slice(3)] },
  ];
}

const SURROGATE_GROUPS = [
  {
    title: "Colour coverage",
    fields: [
      surrogate("cell_edge_delta_e", {
        label: "Colour cell edge",
        exclusiveMin: 0,
        unit: "ΔE",
        help: "About the repeat ΔE of one measurement",
      }),
      surrogate("acquisition", {
        label: "Aim",
        kind: "choice",
        default: SURROGATE_DEFAULTS.acquisition,
        options: [
          { value: "cell", label: "Empty colour cells" },
          { value: "hull_exterior", label: "Outside the gamut hull" },
          { value: COMBINED, label: "Both" },
        ],
        help: "Outside the hull never fills gaps inside it",
      }),
      surrogate("batch_rule", {
        label: "Patches of one generation",
        kind: "choice",
        default: "thompson",
        options: [
          { value: "thompson", label: "One model draw each" },
          { value: "kriging_believer", label: "Predicted colours" },
        ],
        help: "How they avoid aiming at the same cell",
      }),
      surrogate("sigma_floor_delta_e", {
        label: "Spread of a drawn colour",
        min: 0,
        unit: "ΔE",
        help: "Assumed when testing which cell it hits",
      }),
    ],
  },
  {
    title: "Re-marking",
    fields: [
      surrogate("replicate_slots", {
        label: "Re-marked designs per generation",
        kind: "integer",
        min: 0,
        help: "Measured designs marked again, against noise",
      }),
      surrogate("max_measurements_per_design", {
        label: "Measurements per design, at most",
        kind: "integer",
        min: 1,
      }),
    ],
  },
  {
    title: "Candidates",
    fields: [
      surrogate("candidate_count", {
        label: "Candidates per generation",
        kind: "integer",
        min: 1,
        help: "Spread over the design space",
      }),
      surrogate("local_candidates_per_vertex", {
        label: "Variations per hull design",
        kind: "integer",
        min: 0,
        help: "Small changes to designs on the gamut hull",
      }),
      surrogate("local_sigma", {
        label: "Variation size",
        exclusiveMin: 0,
        max: 1,
        help: "Share of each parameter's range",
      }),
      surrogate("thompson_pool_size", {
        label: "Candidates the draws run on",
        kind: "integer",
        min: 1,
        help: "The most promising; time grows with its cube",
      }),
    ],
  },
  {
    title: "Model",
    fields: [
      surrogate("use_usability_model", {
        label: "Usability model",
        kind: "boolean",
        help: "Predicts unmarked, burnt and too-wide patches",
      }),
      surrogate("minimum_usable_for_model", {
        label: "Usable patches before the model",
        kind: "integer",
        min: 1,
        help: "Fewer: evenly spread new designs",
      }),
      surrogate("max_fit_points", {
        label: "Patches for the model fit",
        kind: "integer",
        min: 1,
        help: "Predictions still use every patch",
      }),
      surrogate("maximum_usable_width", {
        label: "Widest usable cluster",
        exclusiveMin: 0,
        unit: "mm",
        clearable: true,
        help: "Empty: the maximum-width rule's limit",
      }),
      surrogate("seed", { label: "Random seed", kind: "integer", min: 0, clearable: true, help: "Empty: different every run" }),
    ],
  },
];

/** The settings of the chosen optimizer as field groups ([] for an optimizer the page does not offer). */
export function optimizerGroups(config) {
  const optimizer = optimizerOf(config);
  if (optimizer === "genetic_algorithm") return [{ title: "", fields: geneticAlgorithmFields(config) }];
  if (optimizer === "ga_prescreen") {
    return [
      { title: "Breeding", fields: geneticAlgorithmFields(config) },
      { title: "Pre-screen", fields: prescreenFields(config) },
    ];
  }
  if (optimizer === "surrogate_coverage_search") return surrogateGroups(config);
  return [];
}

/**
 * Settings read only after another choice: proximity mating reads two more,
 * the aims outside the hull read their share and least usability. Fill their
 * defaults when they are missing.
 */
function withDependentDefaults(config) {
  let next = config;
  if (getAt(next, [...PARENT_PATH, "mate"]) === MATE_BY_PROXIMITY) {
    next = Object.entries(PARENT_DEFAULTS).reduce(
      (acc, [key, value]) => (getAt(acc, [...PARENT_PATH, key]) === undefined ? setAt(acc, [...PARENT_PATH, key], value) : acc),
      next
    );
  }
  if (getAt(next, SURROGATE_PATH)) {
    next = Object.keys(AIM_SETTINGS).reduce((acc, key) => {
      const path = [...SURROGATE_PATH, key];
      return aimReads(acc, key) && getAt(acc, path) === undefined ? setAt(acc, path, SURROGATE_DEFAULTS[key]) : acc;
    }, next);
  }
  return next;
}

const neighbours = (name, help) => ({
  path: [...OBJECTIVES_PATH, name, "num_neighbours"],
  label: "Neighbours",
  kind: "integer",
  min: 1,
  help,
});

/** Every objective the algorithms package computes (objective_functions.compute_objectives). */
export const OBJECTIVES = [
  { key: "chromaticity", title: "Chromaticity", description: "Saturated colours: the chroma C*ab of the measured patch", defaults: null },
  { key: "achromaticity", title: "Achromaticity", description: "Neutral colours: the least chroma", defaults: null },
  { key: "lightness", title: "Lightness", description: "Light colours: a high L*", defaults: null },
  { key: "darkness", title: "Darkness", description: "Dark colours: a low L*", defaults: null },
  { key: "resolution", title: "Resolution", description: "Fine clusters: the narrowest measured width", defaults: null },
  {
    key: "hue_diversity",
    title: "Hue diversity",
    description: "A spread of hues: distance to the nearest neighbours in a*b*",
    defaults: { num_neighbours: 2 },
    fields: [neighbours("hue_diversity", "k nearest neighbours in a*b*")],
  },
  {
    key: "design_space_diversity",
    title: "Design-space diversity",
    description: "A spread over the optimised parameters: distance to the nearest neighbours",
    defaults: { num_neighbours: 2 },
    fields: [neighbours("design_space_diversity", "k nearest neighbours in the design space")],
  },
  { key: "low_energy", title: "Low energy", description: "The least energy per millimetre of cluster", defaults: null },
  {
    key: "closeness_to_target_color",
    title: "Closeness to a target colour",
    description: "The smallest distance to one L*a*b* colour",
    defaults: { target_color: { l_star: 50, a_star: 0, b_star: 0 } },
    columns: 3,
    fields: [
      { path: [...OBJECTIVES_PATH, "closeness_to_target_color", "target_color", "l_star"], label: "L*", min: 0, max: 100 },
      { path: [...OBJECTIVES_PATH, "closeness_to_target_color", "target_color", "a_star"], label: "a*", min: -128, max: 127 },
      { path: [...OBJECTIVES_PATH, "closeness_to_target_color", "target_color", "b_star"], label: "b*", min: -128, max: 127 },
    ],
  },
];

export const OBJECTIVE_LABELS = Object.fromEntries(OBJECTIVES.map((item) => [item.key, item.title]));

const rule = (key, rest) => rest.map((field) => ({ ...field, path: [...RULES_PATH, key, ...field.path] }));

/** Every rule rejection_policy.reject_candidate applies, with the settings it reads. */
export const REJECTION_RULES = [
  {
    key: "reject_on_maximum_width",
    title: "Maximum cluster width",
    description: "Line width + (lines − 1) × hatching must stay below the limit; wider measured clusters score zero",
    defaults: { maximum_allowed_width: 0.08, hypothetical_line_width: 0.001 },
    fields: rule("reject_on_maximum_width", [
      { path: ["maximum_allowed_width"], label: "Maximum cluster width", exclusiveMin: 0, unit: "mm" },
      { path: ["hypothetical_line_width"], label: "Assumed line width", exclusiveMin: 0, unit: "mm" },
    ]),
  },
  {
    key: "reject_on_min_pulses_per_unit_length",
    title: "Minimum pulses per mm",
    description: "frequency ÷ speed must reach the minimum",
    defaults: { minimum_allowed_pulses_per_unit_length: 40 },
    fields: rule("reject_on_min_pulses_per_unit_length", [
      { path: ["minimum_allowed_pulses_per_unit_length"], label: "Minimum pulses per mm", kind: "integer", min: 0 },
    ]),
  },
  {
    key: "reject_on_slow",
    title: "Marking time",
    description: "Seconds to mark one millimetre of cluster (speed, passes, lines) must stay below the limit",
    defaults: { maximum_time_per_unit_cluster: 0.084 },
    fields: rule("reject_on_slow", [
      { path: ["maximum_time_per_unit_cluster"], label: "Per mm of cluster", exclusiveMin: 0, unit: "s" },
      {
        path: ["maximum_time_per_cm_square"],
        label: "Per cm² filled",
        exclusiveMin: 0,
        unit: "s",
        clearable: true,
        help: "Optional: 10 × 10 mm at 0.06 mm cluster width",
      },
    ]),
  },
  {
    key: "reject_on_energy",
    title: "Energy window",
    description: "Pulse energy × pulses per mm × lines × passes must lie in the window",
    defaults: { minimum_energy: 0, maximum_energy: 1 },
    fields: rule("reject_on_energy", [
      { path: ["minimum_energy"], label: "Minimum", min: 0, unit: "J/mm" },
      { path: ["maximum_energy"], label: "Maximum", exclusiveMin: 0, unit: "J/mm" },
    ]),
  },
  {
    key: "reject_on_fluence",
    title: "Fluence window",
    description: "Overexposure ratio R = F₀ / F_th(N) must lie in the window; needs the laser's beam waist",
    defaults: {
      single_pulse_threshold_fluence: 1.74,
      incubation_coefficient: 0.64,
      minimum_overexposure_ratio: 0.5,
      maximum_overexposure_ratio: 60,
    },
    note: "Defaults: AISI 304 stainless steel, fitted on the IPG",
    fields: rule("reject_on_fluence", [
      { path: ["single_pulse_threshold_fluence"], label: "Threshold fluence F_th(1)", exclusiveMin: 0, unit: "J/cm²" },
      { path: ["incubation_coefficient"], label: "Incubation coefficient S", exclusiveMin: 0, max: 1 },
      { path: ["minimum_overexposure_ratio"], label: "Minimum R", min: 0, help: "Below: unmarked" },
      { path: ["maximum_overexposure_ratio"], label: "Maximum R", min: 0, help: "Above: burnt" },
    ]),
  },
  {
    key: "reject_on_explicit_rule",
    title: "Learned bounds",
    description: "Rejects candidates below a point measured unmarked, or above a point measured burnt, in every energy-related parameter",
    defaults: { unmarked: true, burnt: true },
    featureNames: true,
    fields: rule("reject_on_explicit_rule", [
      { path: ["unmarked"], label: "Below a known unmarked point", kind: "boolean" },
      { path: ["burnt"], label: "Above a known burnt point", kind: "boolean" },
    ]),
  },
  {
    key: "reject_on_decision_tree_label",
    title: "Learned classifier",
    description: "A gradient-boosted classifier trained on this run's measured points rejects what it predicts unmarked or burnt",
    defaults: { lgb_params: { n_estimators: 100, num_leaves: 31, learning_rate: 0.1 } },
    featureNames: true,
    columns: 3,
    fields: rule("reject_on_decision_tree_label", [
      { path: ["lgb_params", "n_estimators"], label: "Trees", kind: "integer", min: 1 },
      { path: ["lgb_params", "num_leaves"], label: "Leaves per tree", kind: "integer", min: 2 },
      { path: ["lgb_params", "learning_rate"], label: "Learning rate", exclusiveMin: 0 },
    ]),
  },
];

const HUE_WHEEL_DEFAULTS = {
  start_angle_range: [0, 6.28],
  cone_count_range: [4, 72],
  cone_length: 100,
  avg_diff_threshold: 0.00001,
  min_mc_samples: 100,
  max_mc_samples: 2000,
};

const HUE_WHEEL_FIELDS = [
  { path: [...RANKING_PATH, "cone_count_range", 0], label: "Cones, minimum", kind: "integer", min: 4 },
  { path: [...RANKING_PATH, "cone_count_range", 1], label: "Cones, maximum", kind: "integer", min: 4 },
  { path: [...RANKING_PATH, "cone_length"], label: "Cone length", exclusiveMin: 0 },
  { path: [...RANKING_PATH, "avg_diff_threshold"], label: "Convergence threshold", exclusiveMin: 0 },
  { path: [...RANKING_PATH, "min_mc_samples"], label: "Monte-Carlo samples, minimum", kind: "integer", min: 1 },
  { path: [...RANKING_PATH, "max_mc_samples"], label: "Monte-Carlo samples, maximum", kind: "integer", min: 1 },
];

/** The fields whose key the document has (optional ones only when present). */
export function presentFields(config, fields) {
  return fields.filter((field) => !field.optional || getAt(config, field.path) !== undefined);
}

/** The objectives switched on in the document, by key. */
export function enabledObjectives(config) {
  const objectives = getAt(config, OBJECTIVES_PATH);
  return objectives && typeof objectives === "object" ? Object.keys(objectives) : [];
}

/** The rejection rules switched on in the document, by key. */
export function enabledRules(config) {
  const rules = getAt(config, RULES_PATH);
  return rules && typeof rules === "object" ? REJECTION_RULES.filter((item) => item.key in rules).map((item) => item.key) : [];
}

function Section({ title, caption, children }) {
  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Box>
        <Typography variant="overline" sx={{ display: "block", letterSpacing: 1.2, fontWeight: 700, lineHeight: 1.6, color: INK }}>
          {title}
        </Typography>
        {caption ? (
          <Typography variant="caption" sx={{ display: "block", color: "#64748b" }}>
            {caption}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Stack>
  );
}

export default function OptimizationCard({
  rows,
  valueId,
  onObjectiveChange,
  config,
  onConfigChange,
  onInvalidChange,
  patchGrid = null,
}) {
  const loaded = Boolean(config) && typeof config.optimization === "object" && config.optimization !== null;
  const gridRows = Array.isArray(patchGrid) ? patchGrid[0] : null;
  const gridCols = Array.isArray(patchGrid) ? patchGrid[1] : null;
  const run = useMemo(() => runFields(gridRows != null && gridCols != null ? [gridRows, gridCols] : null), [gridRows, gridCols]);
  const setterFor = useInvalidTotal(onInvalidChange);
  // What a switched-off row held, so switching it on again restores it.
  const stash = useRef({});

  const objectivesOn = useMemo(() => enabledObjectives(config), [config]);
  const rulesOn = useMemo(() => enabledRules(config), [config]);
  const hueWheel = Boolean(getAt(config, [...RANKING_PATH, "use_hue_wheel"]));

  const toggleKey = (basePath, key, defaults, ensureFeatureNames) => (on) => {
    const container = getAt(config, basePath) || {};
    if (on) {
      const kept = stash.current[key];
      let next = setAt(config, [...basePath, key], kept !== undefined ? kept : defaults);
      if (ensureFeatureNames && !Array.isArray(getAt(next, [...RULES_PATH, "feature_names"]))) {
        next = setAt(next, [...RULES_PATH, "feature_names"], FEATURE_NAMES);
      }
      onConfigChange(next);
    } else {
      stash.current[key] = container[key];
      onConfigChange(deleteAt(config, [...basePath, key]));
    }
  };

  const toggleHueWheel = (on) => {
    const ranking = { ...(getAt(config, RANKING_PATH) || {}) };
    if (on) {
      Object.entries(HUE_WHEEL_DEFAULTS).forEach(([key, value]) => {
        if (ranking[key] === undefined) ranking[key] = value;
      });
    }
    ranking.use_hue_wheel = on;
    onConfigChange(setAt(config, RANKING_PATH, ranking));
  };

  const objectiveItems = OBJECTIVES.map((item) => ({
    key: item.key,
    title: item.title,
    description: item.description,
    enabled: objectivesOn.includes(item.key),
    onToggle: toggleKey(OBJECTIVES_PATH, item.key, item.defaults),
    fields: item.fields || [],
    columns: item.columns,
  }));
  const ruleItems = REJECTION_RULES.map((item) => ({
    key: item.key,
    title: item.title,
    description: item.description,
    enabled: rulesOn.includes(item.key),
    onToggle: toggleKey(RULES_PATH, item.key, item.defaults, item.featureNames),
    fields: item.fields,
    note: item.note,
    columns: item.columns,
  }));
  const rankingItems = [
    {
      key: "use_hue_wheel",
      title: "Hue-wheel ranking",
      description: "Monte-Carlo ranking over hue cones instead of a plain non-dominated sort",
      enabled: hueWheel,
      onToggle: toggleHueWheel,
      fields: HUE_WHEEL_FIELDS,
    },
  ];

  // The optimizer: switching away remembers the settings it held, so
  // switching back restores them; a new preset brings its own.
  const optimizer = optimizerOf(config);
  const surrogateChosen = optimizer === "surrogate_coverage_search";
  const remembered = useRef({});
  const chooseOptimizer = (key) => {
    if (key === optimizer) return;
    remembered.current = { ...remembered.current, ...algorithmBlocks(config) };
    onConfigChange(withOptimizer(config, key, remembered.current));
  };
  const groups = optimizerGroups(config);
  const optimizerFields = groups.flatMap((group) => group.fields);
  const titledGroups = groups.some((group) => group.title) ? groups : null;
  const optimizerDescription = optimizer
    ? OPTIMIZERS.find((option) => option.key === optimizer).description
    : `The preset runs ${algorithmName(config) || "no optimizer"}, which this page does not offer; choose one above to replace it.`;

  return (
    <Stack spacing={2.5} sx={{ width: "100%" }}>
      <ChoiceCard
        inputId="exploration-objective"
        label="Optimization preset"
        rows={rows}
        valueId={valueId}
        onChange={onObjectiveChange}
        emptyHint="No optimization presets are available."
      />
      {loaded ? (
        <>
          <Section title="Run" caption="The preset's defaults; change what this run needs">
            <Box sx={{ p: 2, pb: 0.5, border: `1px solid ${HAIRLINE}`, borderRadius: 3, bgcolor: "#ffffff" }}>
              <SettingFields
                config={config}
                fields={presentFields(config, run)}
                onChange={onConfigChange}
                onInvalidChange={setterFor("run")}
                testPrefix="optimization"
                columns={3}
              />
            </Box>
          </Section>
          <Section title="Optimizer" caption="How each next generation is chosen">
            <Box
              data-testid="optimization-optimizer"
              sx={{ p: 2, pb: optimizerFields.length ? 0.5 : 2, border: `1px solid ${HAIRLINE}`, borderRadius: 3, bgcolor: "#ffffff" }}
            >
              <Stack spacing={1.5} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                  {OPTIMIZERS.map((option) => (
                    <Chip
                      key={option.key}
                      label={option.label}
                      variant={optimizer === option.key ? "filled" : "outlined"}
                      color={optimizer === option.key ? "primary" : "default"}
                      onClick={() => chooseOptimizer(option.key)}
                      sx={{ cursor: "pointer" }}
                      data-testid={`optimizer-${option.key}`}
                    />
                  ))}
                </Stack>
                <Typography variant="body2" color="text.secondary" data-testid="optimizer-description">
                  {optimizerDescription}
                </Typography>
                {optimizerFields.length ? (
                  <SettingFields
                    config={config}
                    fields={optimizerFields}
                    groups={titledGroups}
                    onChange={(next) => onConfigChange(withDependentDefaults(next))}
                    onInvalidChange={setterFor("optimizer")}
                    testPrefix="optimization"
                    columns={3}
                  />
                ) : null}
              </Stack>
            </Box>
          </Section>
          <ToggleRows
            title="Objectives"
            caption={
              surrogateChosen
                ? "Surrogate search does not rank by them; their scores are still computed and shown"
                : "What the run optimises for; every objective that is on counts in the ranking"
            }
            hint={`${objectivesOn.length} of ${OBJECTIVES.length} on`}
            items={objectiveItems}
            config={config}
            onChange={onConfigChange}
            onInvalidChange={setterFor("objectives")}
            testPrefix="optimization"
            rowsTestId="optimization-objectives"
          />
          {objectivesOn.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#b42318", fontWeight: 600 }} data-testid="optimization-no-objective">
              Switch on at least one objective.
            </Typography>
          ) : null}
          <ToggleRows
            title="Rejection rules"
            caption="A candidate that fails a rule is never marked"
            hint={`${rulesOn.length} of ${REJECTION_RULES.length} on`}
            items={ruleItems}
            config={config}
            onChange={onConfigChange}
            onInvalidChange={setterFor("rules")}
            testPrefix="optimization"
            rowsTestId="optimization-rules"
          />
          <ToggleRows
            title="Ranking"
            caption={surrogateChosen ? "Genetic algorithm only; Surrogate search does not use it" : ""}
            items={rankingItems}
            config={config}
            onChange={onConfigChange}
            onInvalidChange={setterFor("ranking")}
            testPrefix="optimization"
            rowsTestId="optimization-ranking"
          />
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Choose a preset to load its optimization settings.
        </Typography>
      )}
    </Stack>
  );
}
